import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { useWorkspaces, workspaceError } from '../contexts/WorkspaceContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import './WorkspaceSelector.css';

const storageKey = 'aorra.workspace.invitation';
const invitationPath = '/dashboard/tableros/invitacion';

export default function WorkspaceInvitationDialog() {
  const { user } = useAuth();
  const context = useWorkspaces();
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(() =>
    (location.pathname === invitationPath && location.hash.slice(1)) || sessionStorage.getItem(storageKey) || '');
  const [open, setOpen] = useState(Boolean(token));
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const titleRef = useRef(null);
  const actionPending = useRef(false);

  useEffect(() => {
    if (location.pathname === invitationPath && location.hash.slice(1)) {
      setToken(location.hash.slice(1)); setOpen(true);
    }
  }, [location.pathname, location.hash]);
  useEffect(() => {
    if (!token) return;
    sessionStorage.setItem(storageKey, token);
    let cancelled = false;
    setPreview(null); setError('');
    api.post('/workspaces/invitations/preview', { token })
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch(e => { if (!cancelled) setError(workspaceError(e)); });
    return () => { cancelled = true; };
  }, [token, user?.id]);

  const finish = async action => {
    if (actionPending.current) return;
    actionPending.current = true; setBusy(true); setError('');
    try {
      const { data } = await api.post(`/workspaces/invitations/${action}`, { token });
      if (action === 'accept') {
        await context.refresh(); context.select(data.workspace_id);
      }
      sessionStorage.removeItem(storageKey); setToken(''); setOpen(false);
      navigate('/dashboard', { replace: true });
    } catch (e) { setError(workspaceError(e)); }
    finally { actionPending.current = false; setBusy(false); }
  };
  if (!token) return null;
  return <>
    {!open && <button className="mb-4 text-sm underline text-blue-600" onClick={() => setOpen(true)}>Ver invitación al tablero</button>}
    <Dialog open={open} onOpenChange={value => { if (!busy) setOpen(value); }}>
      <DialogContent className="workspace-dialog grid-cols-1" onOpenAutoFocus={event => { event.preventDefault(); titleRef.current?.focus(); }}>
        <DialogHeader className="workspace-dialog-header min-w-0">
          <DialogTitle ref={titleRef} tabIndex={-1} className="workspace-dialog-title break-words outline-none">Invitación a un tablero</DialogTitle>
          <DialogDescription className="workspace-dialog-description">
            {preview ? <><strong>{preview.owner_name}</strong> te invita a <strong>{preview.workspace_name}</strong>.</> : 'Comparte un espacio para organizar el dinero juntos.'}
          </DialogDescription>
        </DialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {preview ? <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{preview.role === 'viewer' ? 'Podrás consultar las finanzas del tablero.' : 'Podrás consultar, registrar y corregir movimientos del tablero.'} Tus otros tableros seguirán siendo privados.</p>
          <p className="text-sm text-muted-foreground">{preview.subscription_required ? 'Este tablero requiere una suscripción pagada activa.' : 'Puedes participar sin una suscripción propia.'}</p>
          {!preview.eligible && <Link to="/dashboard/planes" onClick={() => setOpen(false)} className="block text-sm underline">Ver planes y activar suscripción</Link>}
          <div className="workspace-dialog-actions">
            <Button className="workspace-button-primary" disabled={busy || !context || !preview.eligible} onClick={() => finish('accept')}>Aceptar invitación</Button>
            <Button className="workspace-button-secondary" disabled={busy} onClick={() => finish('decline')}>Rechazar</Button>
          </div>
          {busy && <p role="status" className="text-sm text-muted-foreground">Guardando…</p>}
        </div> : !error && <p role="status" className="text-sm text-muted-foreground">Consultando invitación…</p>}
      </DialogContent>
    </Dialog>
  </>;
}
